import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { RemissionDetailModel } from 'src/app/models/rental/remission-detail';
import { RemissionModel } from 'src/app/models/rental/remission.model';
import { MaintenanceStrategyService } from 'src/app/services/archive/maintenance-strategy.service';
import { MaintenanceOrderService } from 'src/app/services/maintenance/maintenance-order.service';
import { MaintenanceRoutineService } from 'src/app/services/maintenance/maintenance-routine.service';
import { RemissionDetailService } from 'src/app/services/rental/remission-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-remission-detail-hours-days',
  templateUrl: './remission-detail-hours-days.component.html',
  styleUrls: ['./remission-detail-hours-days.component.scss'],
  standalone: false,

})
export class RemissionDetailHoursDaysComponent implements OnInit {

  public config = inject(DynamicDialogConfig);
  private readonly remissionDetailService = inject(RemissionDetailService);
  private readonly maintenanceOrderService = inject(MaintenanceOrderService);
  private readonly maintenanceRoutineService = inject(MaintenanceRoutineService);
  private readonly maintenanceStrategyService = inject(MaintenanceStrategyService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly toastHandlerService = inject(ToastHandlerService);

  articleObject: { code: number, alterCode: string, name: string } = { code: 0, alterCode: "", name: "" };
  remissionContract: RemissionModel = new RemissionModel();
  remissionDetailContract: RemissionDetailModel = new RemissionDetailModel();
  hourometerCurrent: number = 0;
  totalMaintenanceStrategies: number = 0;
  formRemissionDetail: FormGroup = new FormGroup({});
  suscription: Subscription;
  disableButton: boolean = false;

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.suscription = input$?.subscribe((inputs: any) => {
      this.remissionContract = inputs.remissionContract;
      this.remissionDetailContract = inputs.remissionDetailContract;
      this.validateForm(this.remissionDetailContract);
    });
    await this.getArticle(this.remissionDetailContract.articleId);
    await this.getTotalMaintenanceStrategies();

  }

  changeHourmeter(value: number): boolean {
    if (value < this.hourometerCurrent) {
      this.toastHandlerService.showToast('warning', `El horómetro debe ser mayor al horómetro actual ${this.hourometerCurrent}.`);
      return false;
    }
    return true;
  }

  async getArticle(articleId: number | null) {
    if (articleId != null && articleId != 0) {
      const article = await firstValueFrom(this.maintenanceRoutineService.getArticleById(articleId));
      if (article) {
        this.articleObject = article;
      } else {
        this.articleObject = { code: 0, alterCode: "", name: "Error al buscar el equipo" }
        return;
      }
    } else {
      this.articleObject = { code: 0, alterCode: "", name: "" }
    }
  }

  async getCurrentHourometerAsync() {
    try {
      //Obtengo el utimo horometro para asignarlo o validar si el que viene en la remision es menor al actual
      this.hourometerCurrent = await firstValueFrom(this.maintenanceOrderService.getCurrentHourometerAsync(this.remissionContract.branchId, this.remissionDetailContract.articleId));
      if (this.remissionDetailContract.hourometer == 0 || this.remissionDetailContract.hourometer == null) {
        this.formRemissionDetail.controls['hourometer'].patchValue(this.hourometerCurrent);
      }
    } catch (error) {
      console.error('Error al buscar el horómetro actual.', error);
    }
  }

  async getTotalMaintenanceStrategies() {
    try {
      this.totalMaintenanceStrategies = await firstValueFrom(this.maintenanceStrategyService.getTotalMaintenanceStrategies(this.remissionDetailContract.articleId));
      if (this.totalMaintenanceStrategies == 0) {
        this.toastHandlerService.showToast('warning', 'El equipo no tiene configurado estrategias de mantenimiento.');
        this.formRemissionDetail.disable();
        this.disableButton = true;
        return;
      }
      await this.getCurrentHourometerAsync();
      this.disableButton = false;
    } catch (error) {
      this.formRemissionDetail.disable();
      this.disableButton = true;
      console.error('Error al buscar el total de estrategias de mantenimiento.', error);
    }
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  async send() {
    this.validateFormControls();
    if (!this.formRemissionDetail.valid) {
      this.showValidationMessages();
      return;
    }
    if (this.totalMaintenanceStrategies == 0) {
      this.toastHandlerService.showToast('warning', 'El equipo no tiene configurado estrategias de mantenimiento.');
      return;
    }

    if (!this.changeHourmeter(this.formRemissionDetail.value.hourometer)) {
      return;
    }

    const modelRemissionDetail = this.formRemissionDetail.getRawValue();
    this.remissionDetailContract.hoursDays = modelRemissionDetail.hoursDays;
    this.remissionDetailContract.hourometer = modelRemissionDetail.hourometer;
    try {
      this.remissionDetailContract = await firstValueFrom(this.remissionDetailService.updateRemissionByHoursDays(this.remissionDetailContract));
      this.onReloadEmit();
      this.toastHandlerService.showToast('success', 'El detalle de la remisión fue modificado exitosamente.');
    } catch (error) {
      console.error('Error al actualizar la remisión.', error);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      hoursDays: {
        required: 'El campo horas/días es obligatorio.',
      },
      hourometer: {
        required: 'El campo horómetro es obligatorio.',
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formRemissionDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(remissionDetail: RemissionDetailModel) {
    this.formRemissionDetail = this.formBuilder.group({
      remissionDetailId: [remissionDetail.remissionDetailId],
      hoursDays: [remissionDetail.hoursDays, [Validators.required]],
      hourometer: [remissionDetail.hourometer, [Validators.required, Validators.min(1)]],
    });
  }

  validateFormControls() {
    Object.keys(this.formRemissionDetail.controls).forEach((key) => {
      const control = this.formRemissionDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }

}
